// The tab title (WCAG 2.4.2), for both shells (UI/UX audit, 31 Oct 2026, UX-20 and UX-21).
//
// A shell names the AREA from its route ("Goods in", "Line check"); a page that shows ONE record names
// that record with usePageTitle ("PO024474", "Run 2 · Bhuja Fryer"), and the shell puts it first:
// "PO024474 · Goods in · Quality". Before this the office set only the register's label, so a PO page
// read the same as the register, and the operator shell set nothing at all - after switching from the
// office the tab still said "Goods in · Quality". Moved out of OfficeShell so the operator shell reads
// the same helper rather than a second copy.
import { useEffect, useSyncExternalStore } from 'react';

/** The title before any shell has spoken, and when none names a page. ⚠ The SAME string as
 *  app/index.html's <title> and the manifest's name: one fallback, not two. */
export const APP_TITLE = 'Majans Quality';

/** "PO024474 · Goods in · Quality"; the parts that are empty are left out; none at all = APP_TITLE. */
export function documentTitle(...parts: Array<string | null | undefined>): string {
  const shown = parts.map((p) => (p ?? '').trim()).filter((p) => p !== '');
  return shown.length ? `${shown.join(' · ')} · Quality` : APP_TITLE;
}

/** A shell sets the document title from its parts, most specific first. */
export function useDocumentTitle(...parts: Array<string | null | undefined>): void {
  const title = documentTitle(...parts);
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/* The one record a page is showing, as a tiny store: the page writes it, the shell reads it. A page
 * clears its own name when it unmounts or renames, never another page's - in one navigation the old
 * page's cleanup runs before the new page's effect, so the last writer is the page on screen. */
let current: { owner: symbol; name: string } | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** A detail page names its record ("PO024474"); null while it is not known yet (the area alone shows). */
export function usePageTitle(name: string | null | undefined): void {
  const text = (name ?? '').trim();
  useEffect(() => {
    if (text === '') return;
    const owner = Symbol('page-title');
    current = { owner, name: text };
    emit();
    return () => {
      if (current?.owner === owner) {
        current = null;
        emit();
      }
    };
  }, [text]);
}

/** What the shell shows of the page's record: its name, or null when the page named none. */
export function usePageName(): string | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current?.name ?? null,
    () => null,
  );
}
